import * as IoC from 'typescript-ioc';

import { IApiClient } from '../../infrastructure/api-client';
import {
    IAttendeeLiveSessionDTO,
    IAttendeeLiveSessionParams,
    IAttendeeLiveSessionResponse,
    IControlDAO,
    IControlEventMeetingsDTO,
    IControlMeetingDetailsDTO,
    IControlMeetingRequestParams,
    IGetControlMeetingsRequestParams,
    IRegistrantsListResponse,
    IUpdateMeetingInput
} from '../control.dao';

import { IApiDateConverter } from '../../infrastructure/api-date-converter';
import { RetypePartial } from '../../infrastructure/type-extensions';
import { IEventRequestParams, replaceSortRequestParamsWithSortString, } from '../shared.dto';

import * as MEETINGS_API from '../../constants/api/meetings-api.constants';
import * as ATTENDEE_API from '../../constants/api/attendee-api.constants';
import { IControlAttendeeDetailsDTO } from '../attendees.dao';
import { IAlertsService } from '../../infrastructure/alerts';
import { IMeetingRequestParams, IStartDateTimesDTO } from '../meetings.dao';


type IControlMeetingDetailsDTOInternal = RetypePartial<IControlMeetingDetailsDTO, 'startDateTime' | 'endDateTime', string>;
interface IControlMeetingsDTOInternal {
    content: IControlMeetingDetailsDTOInternal[];
    totalElements: number;
}
type IAttendeeLiveSessionDTOInternal = RetypePartial<IAttendeeLiveSessionDTO, 'joinTime' | 'leftTime', string>;
interface IAttendeeLiveSessionResponseInternal {
    content: IAttendeeLiveSessionDTOInternal[];
    totalCount: number;
    nextPageToken?: string;
}

class ControlDAO extends IControlDAO {
    constructor(
        @IoC.Inject private readonly apiClient: IApiClient,
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter,
    ) {
        super();
    }

    public async getControlEventMeetingsList(params: IGetControlMeetingsRequestParams, signal?: AbortSignal): Promise<IControlEventMeetingsDTO> {
        const paramsWithSortString = replaceSortRequestParamsWithSortString(params);

        const response = await this.apiClient.get<IControlMeetingsDTOInternal>(
            MEETINGS_API.CONTROL_MEETINGS_LIST_URL,
            paramsWithSortString,
            { signal: signal }
        );
        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(x => this.mapInternalMeetingDetailsDTOToPublicDTO(x)),
        };
    }

    public async getControlEventMeeting(params: IControlMeetingRequestParams): Promise<IControlMeetingDetailsDTO> {
        const response = await this.apiClient.get<IControlMeetingDetailsDTOInternal>(
            MEETINGS_API.CONTROL_MEETING_DETAILS_URL,
            { ...params }
        );
        return this.mapInternalMeetingDetailsDTOToPublicDTO(response.parsedResponse);
    }

    public async updateControlEventMeeting(params: IControlMeetingRequestParams & IUpdateMeetingInput): Promise<IControlMeetingDetailsDTO> {
        const response = await this.apiClient.put<IControlMeetingDetailsDTO>(
            MEETINGS_API.CONTROL_MEETING_DETAILS_URL,
            params
        );
        return response.parsedResponse;
    }

    public async getControlEventMeetingsCSV(params: IEventRequestParams & IStartDateTimesDTO) {
        await this.apiClient.getFile(MEETINGS_API.CONTROL_MEETINGS_CSV_URL, params, { requestInit: { method: 'POST' } });
    }

    public async getMeetingStartHours(params: IEventRequestParams): Promise<Date[]> {
        const response = await this.apiClient.get<{ hours: string[] }>(
            MEETINGS_API.CONTROL_MEETINGS_START_HOURS,
            { ...params }
        );
        return response.parsedResponse.hours.map(el => this.apiDateConverter.apiStringToDate(el));
    }

    private mapInternalMeetingDetailsDTOToPublicDTO(internalMeetingsDTO: IControlMeetingDetailsDTOInternal) {
        return {
            ...internalMeetingsDTO,
            startDateTime: this.apiDateConverter.apiStringToDate(internalMeetingsDTO.startDateTime),
            endDateTime: this.apiDateConverter.apiStringToDate(internalMeetingsDTO.endDateTime),
        };
    }

    private mapInternalAttendeeLiveSessionDTOToPublicDTO(internalDTO: IAttendeeLiveSessionDTOInternal): IAttendeeLiveSessionDTO {
        return {
            ...internalDTO,
            joinTime: internalDTO.joinTime && this.apiDateConverter.apiStringToDate(`${internalDTO.joinTime}`),
            leftTime: internalDTO.leftTime && this.apiDateConverter.apiStringToDate(`${internalDTO.leftTime}`),
        };
    }

    public async getControlAttendee(params: IEventRequestParams & { attendeeId: number }): Promise<IControlAttendeeDetailsDTO> {
        try {
            const response = await this.apiClient.get<IControlAttendeeDetailsDTO>(ATTENDEE_API.GET_CONTROL_ATTENDEE_URL, params);
            return response.parsedResponse;
        } catch (err) {
            this.alertsService.alert({
                message: JSON.parse(err.rawResponse).message
            });
        }

    }

    public async getMeetingTypes(params: IEventRequestParams): Promise<string[]> {
        const response = await this.apiClient.get<string[]>(MEETINGS_API.MEETING_TYPES, params);
        return response.parsedResponse;
    }

    public async getAttendeeLiveSessions(params: IAttendeeLiveSessionParams): Promise<IAttendeeLiveSessionResponse> {
        const response = await this.apiClient.get<IAttendeeLiveSessionResponseInternal>(
            MEETINGS_API.CONTROL_MEETING_ATTENDEE_LIVE_SESSIONS_URL,
            { ...params }
        );
        return {
            ...response.parsedResponse,
            content: response.parsedResponse.content.map(x => this.mapInternalAttendeeLiveSessionDTOToPublicDTO(x)),
        };
    }

    public async getMeetingRegistrants(params: IMeetingRequestParams): Promise<IRegistrantsListResponse> {
        const response = await this.apiClient.get<IRegistrantsListResponse>(ATTENDEE_API.GET_MEETING_REGISTRANTS, params);
        return response.parsedResponse;
    }
}

export { ControlDAO };
